import React from "react";
import { getLoginDialogTitleAndDescription } from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type { Props as FollowButtonProps } from "scmp-sdk/components/follow-button";
import { FollowAction } from "scmp-sdk/components/follow-button/enums";
import { isFollowType } from "scmp-sdk/components/follow-button/helpers";
import type { PromoPopupCTAType } from "scmp-sdk/lib/google-analytics/ga4-types";

import { queryFromConfig } from "./actions";
import { StyledFollowButton, StyledRedirectButton } from "./styles";
import type { Campaign } from "./types";

type RendererProps = {
  followButtonProps: Omit<FollowButtonProps, "isFollowing" | "onClick">;
  handlePopupClose: () => void;
};

export const useActionButtonRenderer = (campaign: Campaign) => {
  const {
    config,
    handleCheckIsFollowed,
    handleFollow,
    handleOpenLoginDialog,
    handleTrackGA4,
    user,
  } = useSdkContext();
  const theme = campaign.theme ?? "follow";
  const ctaType = getCTAType(theme);

  const render = (props: RendererProps) => {
    const loginDialogDisplayCampaignName = campaign.name;
    const trackingCampaignName = campaign.campaign;

    switch (theme) {
      case "follow":
        if (!isFollowType(campaign.type)) return;

        const { name, tid, tids = [], type: followType } = campaign;
        const entityIds = tid ? [tid] : tids;
        const isFollowed = entityIds.every(entityId =>
          handleCheckIsFollowed({
            entityId,
            followType,
          }),
        );
        return (
          <StyledFollowButton
            {...props.followButtonProps}
            disabled={isFollowed}
            isFollowing={isFollowed}
            onClick={action => {
              if (action === FollowAction.UnFollow) return;

              handleTrackGA4({
                action: "click",
                category: "follow",
                customized_parameters: {
                  action_type: "follow",
                  follow_type: "topic",
                  internal_campaign: trackingCampaignName,
                  topic_id: tid ?? tids.join(", "),
                  trigger_point: "ppto",
                },
                subcategory: "",
              });

              handleTrackGA4({
                action: "click",
                category: "promopopup",
                customized_parameters: {
                  cta_type: ctaType,
                  internal_campaign: trackingCampaignName,
                  message: "follow",
                  step: "1",
                },
                subcategory: "",
              });

              if (!user) {
                const destination = new URL(globalThis.location.href);
                destination.search = queryFromConfig({ campaign: name });

                const { description, title } = getLoginDialogTitleAndDescription({
                  campaignName: loginDialogDisplayCampaignName,
                  type: followType,
                });

                handleOpenLoginDialog({
                  description,
                  destination: destination.toString(),
                  ga4CustomParameter: {
                    internal_campaign: trackingCampaignName,
                    trigger_point: "ppto",
                  },
                  title,
                });
                return;
              }

              handleFollow({
                action,
                callback: () => {
                  handleTrackGA4({
                    action: "sys",
                    category: "follow",
                    customized_parameters: {
                      action_type: "follow",
                      follow_type: "topic",
                      internal_campaign: trackingCampaignName,
                      topic_id: tid ?? tids.join(", "),
                      trigger_point: "ppto",
                    },
                    subcategory: "result",
                  });
                  if (props.handlePopupClose) props.handlePopupClose();
                },
                entityIds,
                followType,
              });
            }}
          />
        );
      case "link":
        return (
          <StyledRedirectButton
            onClick={() => {
              handleTrackGA4({
                action: "click",
                category: "promopopup",
                customized_parameters: {
                  cta_type: ctaType,
                  destination_url: campaign.content.redirectURL ?? "",
                  internal_campaign: trackingCampaignName,
                  message: campaign.content.redirectTitle ?? "",
                  step: "1",
                },
                subcategory: "",
              });
            }}
            url={campaign.content.redirectURL ?? ""}
          >
            {campaign.content.redirectTitle}
          </StyledRedirectButton>
        );
      case "subscribe":
        const url = new URL(config.subscribe.host);
        url.searchParams.set("module", `semi-FPTO_${trackingCampaignName}`); // Cspell: disable-line
        url.searchParams.set("pgtype", "article");

        url.searchParams.set("promocode", campaign.content.promoCode ?? ""); // Cspell: disable-line
        url.searchParams.set("plan", campaign.content.termCode ?? "");

        return (
          <StyledRedirectButton
            onClick={() => {
              handleTrackGA4({
                action: "click",
                category: "promopopup",
                customized_parameters: {
                  cta_type: ctaType,
                  internal_campaign: trackingCampaignName,
                  message: "Subscribe",
                  step: "1",
                },
                subcategory: "",
              });
            }}
            url={url.toString()}
          >
            Subscribe
          </StyledRedirectButton>
        );
      default:
        return null;
    }
  };

  return {
    render,
  };
};

export const getCTAType = (
  theme: Campaign["theme"],
): Extract<PromoPopupCTAType, "follow" | "subscribe" | "url_redirect"> => {
  switch (theme) {
    case "follow":
      return "follow";
    case "link":
      return "url_redirect";
    case "subscribe":
      return "subscribe";
    default:
      return "follow";
  }
};
